/**
 * Study Viewer — Sprint 2 + 3.
 *
 * Sprint 2 shipped: series rail, instance scroll, CPU window/level.
 * Sprint 3 adds: GPU (WebGL2) renderer + W/L preset bar + hanging-
 * protocol driven default W/L.
 */
import { createEffect, createMemo, createResource, createSignal, For, Show, on } from "solid-js";
import { useParams, A } from "@solidjs/router";
import {
  listStudyInstances, getStudySummary, type StudyInstance,
} from "../api/client";
import { activeStudy, setActiveStudy, updateActiveStudy } from "./studyContext";
import { modalityTitle } from "../core/modality";
import { presetsFor, type WLPreset } from "../protocols/presets";
import { DEFAULT_PROTOCOLS, pickPreset } from "../protocols/engine";
import { cachedPixels, getOrLoad, prefetchNeighbours, warmSeries, cancelWarmer, resetStackCache, stats as cacheStats } from "../viewport/stackCache";
import { MaskCanvas } from "../viewport/MaskCanvas";
import { ClassBar } from "../ui/ClassBar";
import { classes, setActiveId } from "../labeling/classes";
import { ReportPanel } from "../measure/ReportPanel";
import type { Measurement } from "../measure/types";
import { canMeasure, canSignReport } from "../auth/store";
import { createCine } from "../render/cine";
import { bind } from "../shortcuts";
import { onCleanup, onMount } from "solid-js";
import { createViewportStore } from "../viewport/store";
import { Canvas, canvasDrawTick, canvasUploadTick } from "../viewport/Canvas";
import { Canvas3D } from "../viewport/Canvas3D";
import { AnnotationLayer, type Tool } from "../viewport/AnnotationLayer";
import { ToolPalette, type ToolId } from "../ui/ToolPalette";
import { toggleShortcuts } from "./ShortcutHelp";
import { buildVolume, cachedVolume, type BuildProgress } from "../volume/build";
import type { Volume, RenderMode, VolumePreset } from "../volume/types";
import { CT_PRESETS, defaultPreset } from "../volume/presets";
import { specialtyForStudy, configFor, SPECIALTY_CONFIGS, type Specialty } from "../specialty/config";
import { PlaceholderShell } from "../specialty/PlaceholderShell";
import { ReportDraftPanel } from "../ai/ReportDraftPanel";
// pathopod product — sister-modality shells are
// stubbed via _wrongModality so the multi-modality StudyViewer compiles.
// See ../app/_wrongModality.tsx and /ARCHITECTURE.md.
import { WSIViewport }      from "@viewer/wsi/WSIViewport";
import { PathologyShell }   from "@viewer/specialty/PathologyShell";
import { EndoscopyShell, MPRViewport, guessProcedure as _guessProc, type ProcedureType } from "./_wrongModality";
import { auth } from "../auth/store";
import { canSeeSpecialty, singleSpecialty, MODALITY_SPECIALTY } from "../auth/scope";

/** Pick the best procedure guess for an endoscopy study.
 *
 *  Pulls from every signal the viewer has on hand — series
 *  description (modality worklist's per-acquisition label), study
 *  description, and PatientName (many sandbox / worklist setups
 *  encode a short procedure code into the third PN component,
 *  e.g. "VPD^ENDO^COLO" or "DOE^JOHN^EGD"). The underlying
 *  `guessProcedure` does a token-based match across the combined
 *  blob so no one signal has to be canonical. */
function guessEndoProc(
  seriesDesc?: string | null,
  studyDesc?: string | null,
  patientName?: string | null,
): ProcedureType {
  return _guessProc(seriesDesc, studyDesc, patientName);
}

/** Three-letter codes used on the specialty chip. Keeps labels unique
 *  so "RADI" doesn't collide with "Radiology" at a glance. */
const SPECIALTY_CODE: Record<Specialty, string> = {
  radiology:    "RAD",
  pathology:    "PATH",
  endoscopy:    "ENDO",
  radiotherapy: "RTx",
};

export function StudyViewer() {
  const params = useParams<{ studyUid: string }>();

  // --- Break-glass access check (Phase D.3) ---
  // Fires on study mount. If the current user hasn't touched this
  // patient's studies in the configured window, the server returns
  // { requires_justification: true, patient_mrn, ... }. We block
  // the viewport behind a justification modal until the reader
  // types a reason. Their response is ACK'd with an audit event
  // (study.break_glass_ack + metadata.reason) so the FDA evidence
  // record includes the written justification.
  const [bg, setBg] = createSignal<{ needed: boolean; mrn?: string; window_days?: number; ack?: boolean }>({ needed: false });
  const [bgReason, setBgReason] = createSignal("");
  createEffect(async () => {
    const uid = params.studyUid;
    if (!uid) return;
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/access/check?study=${encodeURIComponent(uid)}`, {
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) return;
      const j = await rsp.json() as any;
      if (j.requires_justification) {
        setBg({ needed: true, mrn: j.patient_mrn, window_days: j.window_days, ack: false });
      } else {
        setBg({ needed: false });
      }
    } catch { /* skip on error */ }
  });
  async function ackBreakGlass() {
    const reason = bgReason().trim();
    if (!reason) return;
    const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
    await fetch("/viewer-api/audit/emit", {
      method: "POST",
      headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      body: JSON.stringify({
        kind: "study.break_glass_ack",
        object_ref: params.studyUid,
        patient_mrn: bg().mrn,
        study_uid:   params.studyUid,
        metadata:    { reason, window_days: bg().window_days },
      }),
    }).catch(() => {});
    setBg({ ...bg(), ack: true, needed: false });
  }

  const [instances] = createResource(
    () => params.studyUid,
    async (uid) => listStudyInstances(uid),
  );
  const seriesGroups = createMemo(() => {
    const byUid = new Map<string, StudyInstance[]>();
    for (const it of instances() ?? []) {
      const arr = byUid.get(it.seriesUid) ?? [];
      arr.push(it);
      byUid.set(it.seriesUid, arr);
    }
    return Array.from(byUid.values()).sort((a, b) => a[0].seriesNumber - b[0].seriesNumber);
  });

  const [activeIndex, setActiveIndex] = createSignal(0);
  const activeInstance = createMemo<StudyInstance | undefined>(
    () => (instances() ?? [])[activeIndex()],
  );

  // Publish study context for the app-shell banner. Patient-level
  // fields come from QIDO study summary (one fetch per study); slice
  // counters update reactively as the user scrolls.
  const [summary] = createResource(
    () => params.studyUid,
    async (uid) => (uid ? await getStudySummary(uid) : undefined),
  );
  createEffect(() => {
    const s = summary();
    if (!s) return;
    setActiveStudy({
      studyUid:    s.studyUid,
      patientName: s.patientName || undefined,
      patientId:   s.patientId   || undefined,
      modality:    s.modality    || undefined,
      studyDate:   s.studyDate   || undefined,
      description: s.description || undefined,
      instanceCount: instances()?.length ?? s.instanceCount,
      activeInstance: activeIndex() + 1,
    });
  });
  createEffect(() => {
    updateActiveStudy({
      instanceCount:  instances()?.length,
      activeInstance: activeIndex() + 1,
    });
  });
  onCleanup(() => setActiveStudy(null));
  const activeModality = () => activeInstance()?.modality;
  const presets = () => presetsFor(activeModality());

  // Hanging-protocol pick → default preset.
  const protocolPreset = createMemo<WLPreset | undefined>(() => {
    const inst = activeInstance();
    if (!inst) return undefined;
    const name = pickPreset(DEFAULT_PROTOCOLS, {
      modality: inst.modality,
      description: inst.seriesDescription,
    });
    if (!name) return undefined;
    return presets().find((p) => p.name.toLowerCase() === name.toLowerCase());
  });

  const [manualWC, setManualWC] = createSignal<number | undefined>();
  const [manualWW, setManualWW] = createSignal<number | undefined>();
  const [measurements, setMeasurements] = createSignal<Measurement[]>([]);
  const [activeTool, setActiveTool] = createSignal<Tool | ToolId | "none">("none");
  const [reportOpen, setReportOpen] = createSignal(false);
  const [hu, setHU] = createSignal<{ x: number; y: number; hu: number } | null>(null);
  const [brushRadius, setBrushRadius] = createSignal(10);
  // ---------- 3D volume rendering ----------
  // The volume is built on demand (not on open) because even a fast
  // connection takes ~10 s for 200 slices. Toggling the 3D button
  // kicks off the build against the current active series.
  const [mode3D,       setMode3D]       = createSignal(false);
  const [volume,       setVolume]       = createSignal<Volume | null>(null);
  const [volProgress,  setVolProgress]  = createSignal<BuildProgress | null>(null);
  const [volError,     setVolError]     = createSignal<string | null>(null);
  const [volPreset,    setVolPreset]    = createSignal<VolumePreset>(defaultPreset());
  const [volWc,        setVolWc]        = createSignal<number>(defaultPreset().wc);
  const [volWw,        setVolWw]        = createSignal<number>(defaultPreset().ww);
  const [volMode,      setVolMode]      = createSignal<RenderMode>(defaultPreset().mode);
  function applyVolPreset(p: VolumePreset) {
    setVolPreset(p);
    setVolWc(p.wc);
    setVolWw(p.ww);
    setVolMode(p.mode);
  }
  // Stable-reference memo — consumers like PathologyShell's
  // createResource key off the array identity, so returning a fresh
  // array every render would re-fire `buildPyramid` forever and
  // leave the viewport stuck on "Indexing pyramid…".
  const activeSeriesInstancesMemo = createMemo(() => {
    const list = instances() ?? [];
    const inst = activeInstance();
    if (!inst) return [] as StudyInstance[];
    return list.filter((i) => i.seriesUid === inst.seriesUid);
  });
  const activeSeriesInstances = () => activeSeriesInstancesMemo();
  async function toggle3D() {
    const next = !mode3D();
    setMode3D(next);
    if (!next) return;
    // Re-use volume if already built for this series.
    const series = activeSeriesInstances();
    if (!series.length) { setVolError("no instances in active series"); return; }
    const hit = cachedVolume(series[0].seriesUid);
    if (hit) { setVolume(hit); setVolError(null); return; }
    setVolume(null);
    setVolError(null);
    try {
      const v = await buildVolume(series, (p) => setVolProgress(p));
      setVolume(v);
    } catch (e) {
      setVolError(String(e));
    }
  }
  const [seriesRailCollapsed, setSeriesRailCollapsed] = createSignal(
    (() => { try { return localStorage.getItem("vpd.seriesRailCollapsed") === "1"; } catch { return false; }})(),
  );

  // ---------- Specialty skin ----------
  // Auto-detect from the union of modalities present in the study; the
  // reader can override with the chip in the top strip. Stored as a
  // manual override that takes precedence over detection when set.
  const [specialtyOverride, setSpecialtyOverride] = createSignal<Specialty | null>(null);
  const detectedSpecialty = createMemo<Specialty>(() => {
    const mods = (instances() ?? []).map((i) => i.modality);
    return specialtyForStudy(mods);
  });
  // Single-modality product: there is exactly one specialty
  // available to any User-UI role. The chip row is kept for visual
  // continuity but contains a single chip.
  const soleSpecialty   = createMemo(() => singleSpecialty(auth()));
  const availableChips  = createMemo<Specialty[]>(() =>
    soleSpecialty() ? [MODALITY_SPECIALTY] : [],
  );
  const specialty = createMemo<Specialty>(() => {
    const forced = soleSpecialty();
    if (forced) return forced;
    const picked = specialtyOverride() ?? detectedSpecialty();
    return canSeeSpecialty(auth(), picked) ? picked : (availableChips()[0] ?? "radiology");
  });
  const specialtyCfg = () => configFor(specialty());

  // ---------- AI Report panel ----------
  const [aiPanelCollapsed, setAiPanelCollapsed] = createSignal(
    (() => { try { return localStorage.getItem("vpd.aiPanelCollapsed") === "1"; } catch { return false; }})(),
  );
  createEffect(() => {
    try { localStorage.setItem("vpd.aiPanelCollapsed", aiPanelCollapsed() ? "1" : "0"); } catch { /* fine */ }
  });

  function jumpToInstance(instanceUid: string, _frame?: number) {
    const list = instances() ?? [];
    const idx = list.findIndex((x) => x.instanceUid === instanceUid);
    if (idx >= 0) setActiveIndex(idx);
  }

  // Auto-build the volume when the user enters the RT specialty, so
  // the MPR panes have data without the user also having to toggle 3D.
  createEffect(() => {
    if (specialty() !== "radiotherapy") return;
    const list = activeSeriesInstances();
    if (!list.length) return;
    const hit = cachedVolume(list[0].seriesUid);
    if (hit) { setVolume(hit); return; }
    if (volume()) return;
    buildVolume(list, (pr) => setVolProgress(pr))
      .then((v) => setVolume(v))
      .catch((e) => setVolError(String(e)));
  });
  createEffect(() => {
    try { localStorage.setItem("vpd.seriesRailCollapsed", seriesRailCollapsed() ? "1" : "0"); } catch { /* fine */ }
  });
  const cine = createCine(15);
  const viewport = createViewportStore();

  // Tools that surface the class/brush bar above the viewport.
  const isLabelTool = () =>
    ["bbox", "polygon", "keypoint", "mask"].includes(activeTool() as string);

  // -----------------------------------------------------------------
  //  Pixel loading — cached per-instance, with neighbour prefetch.
  //
  //  A stack of 120 decoded slices is held in an LRU Map keyed by
  //  SOP Instance UID. When the user lands on slice N we also
  //  prefetch ±3 in the background so the next scroll is already
  //  decoded. `cachedPixels()` is a cheap synchronous read used for
  //  instant display when the active slice is in the cache.
  // -----------------------------------------------------------------
  const [pixels, setPixels] = createSignal<ReturnType<typeof cachedPixels>>(null);
  const [pixelsLoading, setPixelsLoading] = createSignal(false);
  const [pixelsError, setPixelsError] = createSignal<string | null>(null);

  // When the study changes, drop the cache so we don't serve stale
  // pixels under new UIDs.  Explicit `on()` to immunise against a
  // production-minifier pass pruning a bare-expression dep.
  createEffect(on(() => params.studyUid, () => resetStackCache()));

  // The stack cache (decodes a whole series into a DecodedPixels[]) is
  // only meaningful for modalities that render *one frame per
  // instance* — radiology CT/MR, endoscopy stills, RT CT bases. For
  // pathology the series' "instances" are pyramid resolution levels of
  // one slide; downloading each full DICOM blob would fire 6 parallel
  // 200 MB+ transfers, crash `vpd-dicomweb` under memory pressure, and
  // pointlessly duplicate the work that `WSIPyramidViewport` already
  // does via the per-frame WADO endpoint. The pyramid path is
  // self-contained — skip the stack cache entirely.
  // Endoscopy streams MPEG-4 / H.264 video through HTMLVideoElement,
  // not the radiology single-frame decoder — warming the stack cache
  // against multi-frame Video Endoscopic instances would try to parse
  // a 200 MB MP4 blob as an uncompressed-LE image and blow the stack.
  const usesStackCache = () => specialty() !== "pathology" && specialty() !== "endoscopy";

  // On every activeInstance change: try the cache; if missing, load.
  //
  // We bind the dep explicitly via `on(() => activeInstance(), ...)`
  // rather than reading `activeInstance()` inside the effect body —
  // the bare-read form has been pruned by Vite's prod minifier in
  // this project before (see the Canvas.tsx comment), causing the
  // effect to run only on mount. Symptom: the slider / counter
  // advances because *it* tracks activeIndex directly, but the image
  // stays stuck on whichever slice was loaded at mount.
  createEffect(on(
    () => activeInstance(),
    (inst) => {
      if (!inst) { setPixels(null); return; }
      if (!usesStackCache()) { setPixels(null); return; }
      const hit = cachedPixels(inst.instanceUid);
      if (hit) {
        setPixels(hit);
        setPixelsError(null);
        return;
      }
      setPixelsLoading(true);
      setPixelsError(null);
      getOrLoad(inst).then((p) => {
        if (activeInstance()?.instanceUid === inst.instanceUid) {
          setPixels(p);
        }
      }).catch((e) => {
        if (activeInstance()?.instanceUid === inst.instanceUid) {
          setPixelsError(String(e));
          setPixels(null);
        }
      }).finally(() => setPixelsLoading(false));
    },
  ));

  // Background prefetch of the neighbours every time the active
  // index changes. Non-blocking; failures are swallowed.
  createEffect(() => {
    if (!usesStackCache()) return;
    const list = instances() ?? [];
    if (!list.length) return;
    prefetchNeighbours(list, activeIndex(), 4);
  });

  // One-time full-series warmer — fires ONLY when the instance list
  // first becomes available. Tracking activeIndex() here was the
  // previous bug: every scroll re-fired the effect, which re-called
  // warmSeries and cancelled the in-flight warmer via the
  // generation counter. Now the warmer starts once and runs to
  // completion unless the study is switched.
  createEffect(on(() => instances(), (list) => {
    if (!usesStackCache()) return;
    if (!list || !list.length) return;
    warmSeries(list, activeIndex());
  }));
  onCleanup(() => cancelWarmer());

  // Effective W/L — manual overrides protocol preset overrides decoder default.
  const effectiveWC = createMemo(() => {
    if (manualWC() !== undefined) return manualWC()!;
    if (protocolPreset()) return protocolPreset()!.wc;
    const p = pixels();
    if (!p) return 40;
    const lo = p.minStored * p.rescaleSlope + p.rescaleIntercept;
    const hi = p.maxStored * p.rescaleSlope + p.rescaleIntercept;
    return (lo + hi) / 2;
  });
  const effectiveWW = createMemo(() => {
    if (manualWW() !== undefined) return manualWW()!;
    if (protocolPreset()) return protocolPreset()!.ww;
    const p = pixels();
    if (!p) return 400;
    const lo = p.minStored * p.rescaleSlope + p.rescaleIntercept;
    const hi = p.maxStored * p.rescaleSlope + p.rescaleIntercept;
    return Math.max(1, hi - lo);
  });

  const applyPreset = (p: WLPreset) => {
    setManualWC(p.wc);
    setManualWW(p.ww);
  };
  const resetWL = () => {
    setManualWC(undefined);
    setManualWW(undefined);
  };
  const onWindowChange = (wc: number, ww: number) => {
    setManualWC(wc);
    setManualWW(ww);
  };

  // Reset the manual override when the user jumps series so the next
  // series picks up its own protocol preset automatically.
  createEffect(() => {
    activeInstance()?.seriesUid;
    setManualWC(undefined);
    setManualWW(undefined);
  });

  // ---------------------------------------------------------------
  //  Keyboard shortcuts — scoped to the viewer page.
  // ---------------------------------------------------------------
  // Cycle through the active modality's presets; wraps around.
  const cyclePreset = () => {
    const list = presets();
    if (!list.length) return;
    const idx = list.findIndex((p) => p.wc === manualWC() && p.ww === manualWW());
    const next = list[(idx + 1 + list.length) % list.length];
    applyPreset(next);
  };

  onMount(() => {
    const stackMax = () => (instances()?.length ?? 1);
    const scrollBy = (d: number) =>
      setActiveIndex((i) => Math.max(0, Math.min(stackMax() - 1, i + d)));
    const applyPresetIdx = (i: number) => {
      const p = presets()[i];
      if (p) { setManualWC(p.wc); setManualWW(p.ww); }
    };
    const un = [
      bind("ArrowUp",   (e) => { e.preventDefault(); scrollBy(-1); }),
      bind("ArrowDown", (e) => { e.preventDefault(); scrollBy(+1); }),
      bind("d",         () => toggle3D()),
      bind("Home",      () => setActiveIndex(0)),
      bind("End",       () => setActiveIndex(stackMax() - 1)),
      bind("1", () => applyPresetIdx(0)),
      bind("2", () => applyPresetIdx(1)),
      bind("3", () => applyPresetIdx(2)),
      bind("4", () => applyPresetIdx(3)),
      bind("5", () => applyPresetIdx(4)),
      bind("l", () => canMeasure() && setActiveTool((t) => t === "length"   ? "none" : "length")),
      bind("a", () => canMeasure() && setActiveTool((t) => t === "angle"    ? "none" : "angle")),
      bind("A", () => canMeasure() && setActiveTool((t) => t === "cobb"     ? "none" : "cobb")),  // Shift+A without the modifier guard
      bind("Shift+A", () => canMeasure() && setActiveTool((t) => t === "cobb" ? "none" : "cobb")),
      bind("e", () => canMeasure() && setActiveTool((t) => t === "ellipse"  ? "none" : "ellipse")),
      bind("b", () => canMeasure() && setActiveTool((t) => t === "rect"     ? "none" : "rect")),
      bind("x", () => canMeasure() && setActiveTool((t) => t === "freehand" ? "none" : "freehand")),
      // Labeling tools
      bind("t", () => canMeasure() && setActiveTool((t) => t === "text"  ? "none" : "text")),
      bind("n", () => canMeasure() && setActiveTool((t) => t === "arrow" ? "none" : "arrow")),
      bind("k", () => canMeasure() && setActiveTool((t) => t === "pin"   ? "none" : "pin")),
      // ML dataset tools + class selector shortcuts (1-9)
      bind("o", () => canMeasure() && setActiveTool((t) => t === "bbox"     ? "none" : "bbox")),
      bind("g", () => canMeasure() && setActiveTool((t) => t === "polygon"  ? "none" : "polygon")),
      bind("j", () => canMeasure() && setActiveTool((t) => t === "keypoint" ? "none" : "keypoint")),
      bind("u", () => canMeasure() && setActiveTool((t) => t === "mask"     ? "none" : "mask")),
      ...classes().slice(0, 9).map((c, i) =>
        bind(String(i + 1), () => setActiveId(c.id))),
      bind("m", () => setActiveTool((t) => t === "pan"    ? "none" : "pan")),
      bind("z", () => setActiveTool((t) => t === "zoom"   ? "none" : "zoom")),
      bind("w", () => setActiveTool((t) => t === "window" ? "none" : "window")),
      bind("p", () => cyclePreset()),
      bind("i", () => viewport.setInvert(!viewport.invert())),
      bind("f", () => viewport.setFlipH(!viewport.flipH())),
      bind("v", () => viewport.setFlipV(!viewport.flipV())),
      bind("r", () => { viewport.reset(); setManualWC(undefined); setManualWW(undefined); }),
      bind("c", () => setMeasurements([])),
      bind("Escape", () => setActiveTool("none")),
      bind(" ", (e) => {
        e.preventDefault();
        cine.toggle(setActiveIndex, stackMax);
      }),
    ];
    onCleanup(() => { un.forEach((fn) => fn()); cine.stop(); });
  });

  const scroll = (e: WheelEvent) => {
    if ((e.target as HTMLElement).closest(".thumb-rail")) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, Math.min((instances()?.length ?? 1) - 1, i + (e.deltaY > 0 ? 1 : -1))));
    }
  };

  // ----------------------------------------------------------------------
  // Series-aware slice navigation helpers used by the viewport slider.
  // A study may contain N series, each with its own slice range. The
  // slider in the viewport scrubs WITHIN the active series — i.e. the
  // series whose instance the user is currently viewing.
  // ----------------------------------------------------------------------
  const seriesOf = (seriesUid: string) =>
    (instances() ?? []).filter((x) => x.seriesUid === seriesUid);
  const activeSeriesStart = createMemo(() => {
    const list = instances() ?? [];
    const inst = activeInstance();
    if (!inst) return 0;
    return list.findIndex((x) => x.seriesUid === inst.seriesUid);
  });
  const activeSeriesLen = createMemo(() => {
    const inst = activeInstance();
    if (!inst) return 0;
    return seriesOf(inst.seriesUid).length;
  });
  const activeSliceInSeries = () => activeIndex() - activeSeriesStart();

  return (
    <div class="relative flex h-full" onWheel={scroll}>
      {/* Break-glass justification modal (Phase D.3). Blocks every
          pointer interaction below until the reader enters a reason
          or decides not to open the study. Audit row is fired when
          the modal is acknowledged — see study.break_glass_ack in
          vaultpod/docs/regulatory/admin_audit_evidence.md §3. */}
      <Show when={bg().needed && !bg().ack}>
        <div class="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm">
          <div class="w-[min(560px,92vw)] rounded-lg border border-warn bg-ink-100 p-5 shadow-2xl">
            <div class="flex items-center gap-2">
              <span class="chip-warn">Break-glass access</span>
              <span class="text-[11px] text-ink-700">HIPAA §164.308 · 21 CFR Part 11 §11.10(e)</span>
            </div>
            <h2 class="mt-3 text-base font-semibold text-white">
              This is your first access to this patient in {bg().window_days} days.
            </h2>
            <p class="mt-2 text-[12px] leading-relaxed text-ink-700">
              Patient <code class="text-white">{bg().mrn}</code> is not on
              your recent reading roster. Provide a written justification
              (consult, coverage, QA review, etc.) before viewing their
              images. Your reason and identity are recorded in the audit
              log.
            </p>
            <label class="mt-3 flex flex-col gap-1">
              <span class="text-[10px] uppercase tracking-wider text-ink-700">Reason</span>
              <textarea class="min-h-[90px] rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[12px] text-white focus:border-brand focus:outline-none"
                        placeholder="e.g. Requested consult from Dr X, weekend coverage, tumor board preparation…"
                        value={bgReason()}
                        onInput={(e) => setBgReason(e.currentTarget.value)} />
            </label>
            <div class="mt-3 flex items-center justify-end gap-2">
              <A href="/studies" class="btn-ghost text-xs">Cancel</A>
              <button class="btn-primary text-xs disabled:opacity-50"
                      disabled={!bgReason().trim()}
                      onClick={ackBreakGlass}>Acknowledge + open</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Series rail ---------------------------------------------- */}
      {/* Pathology has a single slide per study and a dedicated
          folder-tree panel inside PathologyShell — rendering the
          series rail on top just adds noise (one-item list). Hide
          it for SM and let the shell own the left chrome. */}
      <Show when={specialty() !== "pathology"}>
      <aside class="thumb-rail relative flex shrink-0 flex-col border-r border-ink-300 bg-ink-100 transition-[width] duration-150"
             classList={{ "w-56": !seriesRailCollapsed(), "w-12": seriesRailCollapsed() }}>
        <button type="button"
                onClick={() => setSeriesRailCollapsed((v) => !v)}
                aria-expanded={!seriesRailCollapsed()}
                aria-label={seriesRailCollapsed() ? "Expand series rail" : "Collapse series rail"}
                title={seriesRailCollapsed() ? "Expand series list" : "Collapse series list"}
                class="absolute -right-3 top-3 z-10 grid h-6 w-6 place-items-center rounded-full border border-ink-300 bg-ink-100 text-ink-700 shadow hover:text-white">
          <span class="text-xs">{seriesRailCollapsed() ? "›" : "‹"}</span>
        </button>
        <Show when={!seriesRailCollapsed()}>
          <div class="border-b border-ink-300 px-3 py-2 text-[11px]">
            <div class="text-ink-700">Study</div>
            <code class="break-all font-mono text-[10px] text-ink-900">{params.studyUid}</code>
          </div>
        </Show>
        <div class="flex-1 overflow-auto p-2 text-xs">
          <Show when={!instances.loading} fallback={<div class="p-4 text-ink-700">loading…</div>}>
            <For each={seriesGroups()}>{(group) => {
              const active = () => activeInstance()?.seriesUid === group[0].seriesUid;
              const go = () => {
                const list = instances() ?? [];
                setActiveIndex(list.findIndex((x) => x.seriesUid === group[0].seriesUid));
              };
              return (
                <Show when={!seriesRailCollapsed()} fallback={
                  <button type="button"
                          onClick={go}
                          title={`${modalityTitle(group[0].modality)} · S${group[0].seriesNumber} · ${group.length} slices · ${group[0].seriesDescription || ""}`}
                          class="mb-1 flex h-9 w-full items-center justify-center rounded border transition"
                          classList={{
                            "border-brand bg-brand/15 text-white": active(),
                            "border-ink-300 text-ink-800 hover:bg-ink-200 hover:text-white": !active(),
                          }}>
                    <span class="font-mono text-[10px]">{group[0].modality || "—"}</span>
                  </button>
                }>
                  <button type="button"
                          onClick={go}
                          class="mb-2 block w-full rounded border px-2 py-2 text-left transition"
                          classList={{
                            "border-brand bg-brand/15 text-white": active(),
                            "border-ink-300 hover:bg-ink-200 hover:text-white": !active(),
                          }}>
                    <div class="mb-1 flex items-center gap-1.5">
                      <span class="chip-brand !text-[9px]"
                            title={modalityTitle(group[0].modality)}>
                        {group[0].modality || "—"}
                      </span>
                      <span class="text-[10px] text-ink-700">S{group[0].seriesNumber}</span>
                      <span class="ml-auto rounded bg-ink-200 px-1.5 py-0.5 font-mono text-[10px] text-ink-900">
                        {group.length}
                      </span>
                    </div>
                    <div class="truncate text-[11px] leading-snug">
                      {group[0].seriesDescription || "(no description)"}
                    </div>
                  </button>
                </Show>
              );
            }}</For>
          </Show>
        </div>
        <Show when={!seriesRailCollapsed()}>
          <div class="border-t border-ink-300 p-2 text-[10px] leading-snug text-ink-700">
            click a series · wheel on image to scrub slices
          </div>
        </Show>
      </aside>
      </Show>

      {/* Tool palette (docked left) — radiology-only. Pathology ships
          its own vertical annotation toolbar inside WSIViewport;
          endoscopy + RT have their own in-viewport controls too. */}
      <Show when={specialty() === "radiology"}>
        <ToolPalette
          activeTool={activeTool() as any}
          onTool={(t) => setActiveTool((cur) => cur === t ? "none" : t)}
          cinePlaying={cine.playing()}
          onCineToggle={() => cine.toggle(setActiveIndex, () => instances()?.length ?? 1)}
          onReset={() => { viewport.reset(); setManualWC(undefined); setManualWW(undefined); }}
          onInvert={() => viewport.setInvert(!viewport.invert())}
          onFlipH={() => viewport.setFlipH(!viewport.flipH())}
          onFlipV={() => viewport.setFlipV(!viewport.flipV())}
          onClear={() => setMeasurements([])}
          onPreset={cyclePreset}
          onHelp={toggleShortcuts}
          canMeasure={canMeasure()}
          canSignReport={canSignReport()}
          onSign={() => setReportOpen(true)}
          stackPrev={() => setActiveIndex((i) => Math.max(0, i - 1))}
          stackNext={() => setActiveIndex((i) => Math.min((instances()?.length ?? 1) - 1, i + 1))}
          mode3D={mode3D()}
          onToggle3D={toggle3D}
        />
      </Show>

      {/* Viewport column ---------------------------------------------- */}
      <div class="flex min-w-0 flex-1 flex-col">
        {/* The radiology-style status strip (slice / W-L / zoom / preset
            chips) is only meaningful for radiology SOPs. Pathology,
            endoscopy and RT each bring their own specialty-specific top
            bar, so we hide this one to avoid mixed-message chrome. */}
        <Show when={specialty() === "radiology"}>
        <div class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-1.5 text-[11px] text-ink-700">
          {/* Slice scrub — MedDream-style. Operates on the active series
              so a user who's looking at S3 scrubs within S3's slices
              only. `input` is throttled by the browser; setting the
              index triggers cache lookup or fetch as usual. */}
          <Show when={activeSeriesLen() > 1} fallback={
            <span>instance <span class="font-mono text-ink-900">{activeIndex() + 1}</span> / {instances()?.length ?? 0}</span>
          }>
            <label class="flex items-center gap-2"
                   title="Scrub active series — drag, arrow keys, or scroll wheel"
                   onWheel={(e) => {
                     // Browsers disagree on whether <input type=range>'s
                     // onInput fires for wheel events, so handle wheel
                     // ourselves and keep activeIndex authoritative.
                     e.preventDefault();
                     const n = activeSeriesLen();
                     const start = activeSeriesStart();
                     const cur  = activeIndex() - start;
                     const next = Math.max(0, Math.min(n - 1, cur + (e.deltaY > 0 ? 1 : -1)));
                     setActiveIndex(start + next);
                   }}>
              <span class="text-[10px] uppercase tracking-wider text-ink-700">slice</span>
              <input type="range"
                     min="0" max={activeSeriesLen() - 1} step="1"
                     value={activeSliceInSeries()}
                     onInput={(e) => {
                       const v = parseInt((e.currentTarget as HTMLInputElement).value, 10);
                       setActiveIndex(activeSeriesStart() + v);
                     }}
                     onChange={(e) => {
                       // Safari/Firefox quirk: some input methods only fire
                       // `change`. Binding both keeps the slider authoritative.
                       const v = parseInt((e.currentTarget as HTMLInputElement).value, 10);
                       setActiveIndex(activeSeriesStart() + v);
                     }}
                     class="h-1 w-48 accent-brand" />
              <span class="font-mono text-ink-900">
                {activeSliceInSeries() + 1}
              </span>
              <span class="text-ink-700">/ {activeSeriesLen()}</span>
            </label>
          </Show>
          <Show when={pixels()}>
            <span>·</span>
            <span>{pixels()!.width} × {pixels()!.height}</span>
            <span>·</span>
            <span>{pixels()!.photometric}</span>
            <Show when={pixels()!.photometric !== "RGB"}>
              <span>·</span>
              <span>W/L <span class="font-mono text-ink-900">{effectiveWW().toFixed(0)} / {effectiveWC().toFixed(0)}</span></span>
            </Show>
            <span>·</span>
            <span>zoom <span class="font-mono text-ink-900">{(viewport.zoom() * 100).toFixed(0)}%</span></span>
          </Show>

          <Show when={activeModality() && pixels()?.photometric !== "RGB"}>
            <span class="mx-2 h-4 w-px bg-ink-300" />
            <span class="text-ink-700">W/L preset:</span>
            <For each={presets()}>{(p) => (
              <button type="button"
                      class="rounded border border-ink-300 px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-ink-200 hover:text-white"
                      classList={{ "bg-brand text-white border-brand": manualWC() === p.wc && manualWW() === p.ww }}
                      onClick={() => applyPreset(p)}>
                {p.name}
              </button>
            )}</For>
            <button class="btn-ghost text-[10px]" onClick={resetWL}>Auto</button>
          </Show>

          {/* Specialty picker — auto-detected from the study's
              modality but override-able within the user's allowed
              scope. A single-specialty user (e.g. a pathologist)
              sees a locked badge instead of a switcher. */}
          <Show when={soleSpecialty()} fallback={
            <Show when={availableChips().length > 1}>
              <span class="ml-auto inline-flex items-center gap-1">
                <span class="text-[10px] uppercase tracking-wider text-ink-700">specialty:</span>
                <For each={availableChips()}>{(s) => (
                  <button type="button"
                          class="rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider transition"
                          classList={{
                            "bg-brand text-white border-brand":              specialty() === s,
                            "border-ink-300 text-ink-700 hover:text-white":  specialty() !== s,
                          }}
                          title={SPECIALTY_CONFIGS[s].description}
                          onClick={() => setSpecialtyOverride(s)}>
                    {SPECIALTY_CODE[s]}
                  </button>
                )}</For>
                <button type="button"
                        class="btn-ghost !text-[9px]"
                        title="Reset to auto-detected specialty"
                        onClick={() => setSpecialtyOverride(null)}>
                  auto
                </button>
              </span>
            </Show>
          }>
            <span class="ml-auto inline-flex items-center gap-1.5"
                  title={`Your role is scoped to ${SPECIALTY_CONFIGS[soleSpecialty()!].label}; other specialties aren't visible.`}>
              <span class="text-[10px] uppercase tracking-wider text-ink-700">specialty:</span>
              <span class="rounded border border-brand bg-brand/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-white">
                {SPECIALTY_CODE[soleSpecialty()!]}
              </span>
              <span class="text-ink-700">·</span>
              <span class="text-[10px] text-ink-700">role-locked</span>
            </span>
          </Show>

          <span class="w-full text-[10px] text-ink-700">
            wheel = scroll slices · ctrl+wheel = zoom · right-drag = W/L · alt-drag = pan · dbl-click = reset
          </span>
        </div>
        </Show>

        {/* Class selector + brush size — visible only when a label tool is active + the
            user is in a radiology-adjacent viewport (pathology has its own class chrome). */}
        <Show when={specialty() === "radiology" && isLabelTool()}>
          <ClassBar
            brushRadius={activeTool() === "mask" ? brushRadius() : undefined}
            setBrushRadius={activeTool() === "mask" ? setBrushRadius : undefined}
          />
        </Show>

        {/* Viewport.  `overflow-hidden` is important: when zoomed in,
            MaskCanvas / AnnotationLayer are CSS-scaled beyond the image
            frame, and without clipping their hit regions extend onto
            the toolbar / class bar so a brush stroke could "bleed"
            outside the image.
            Plain-wheel slice-step lives here (not on the canvas)
            because AnnotationLayer / MaskCanvas can sit on top with
            `pointer-events: auto` when a tool is active, blocking the
            wheel from reaching the canvas. Every layer bubbles up to
            this container, so it always gets the event. */}
        <div class="relative min-h-0 flex-1 overflow-hidden"
             onWheel={(e) => {
               // Slice-stepping is a RADIOLOGY gesture only.
               // Pathology + endoscopy + radiotherapy own their
               // own wheel handlers inside their viewports
               // (pathology zooms; endoscopy seeks; RT MPR steps),
               // and they need plain wheel events outside the canvas
               // (in the right-dock Report / AI panels) to scroll
               // natively. Bail out unless we're looking at radiology.
               if (specialty() !== "radiology") return;
               // Likewise, even within radiology, only intercept
               // wheels that originate over the imaging canvas /
               // overlay layers — leave events on the right-dock or
               // the toolbar alone so dock content scrolls normally.
               const tgt = e.target as HTMLElement;
               if (tgt.closest("aside, [role=tablist], [data-scroll-region]")) return;

               // All viewport wheels land here — even when a drawing
               // tool is active and AnnotationLayer captured the
               // original event with pointer-events: auto. Plain
               // wheel steps the slice stack; Ctrl/⌘+wheel zooms
               // about the cursor.
               if (e.ctrlKey || e.metaKey) {
                 e.preventDefault();
                 const host = e.currentTarget as HTMLElement;
                 const rect = host.getBoundingClientRect();
                 const mx = e.clientX - rect.left, my = e.clientY - rect.top;
                 const before = viewport.screenToImage(mx, my);
                 const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
                 viewport.setZoom(Math.max(0.1, Math.min(20, viewport.zoom() * factor)));
                 const after = viewport.screenToImage(mx, my);
                 const dx = (after.x - before.x) * viewport.scale();
                 const dy = (after.y - before.y) * viewport.scale();
                 viewport.setPan({ x: viewport.pan().x + dx, y: viewport.pan().y + dy });
                 return;
               }
               e.preventDefault();
               const n = instances()?.length ?? 1;
               if (n <= 1) return;
               setActiveIndex((i) => Math.max(0, Math.min(n - 1, i + (e.deltaY > 0 ? 1 : -1))));
             }}>
          {/* ---------------- Pathology (WSI shell) ---------------- */}
          {/* Pyramid-aware pathology shell — fetches tiles on-demand,
              no full-instance decode. The 2-D `pixels()` path is no
              longer part of this branch. */}
          <Show when={specialty() === "pathology"}>
            <PathologyShell
              studyUid={params.studyUid}
              caseId={activeInstance()?.seriesDescription ?? params.studyUid}
              slides={activeSeriesInstances()}
              activeSlideUid={activeInstance()?.instanceUid ?? ""}
              onPickSlide={(uid) => {
                const list = instances() ?? [];
                const idx = list.findIndex((x) => x.instanceUid === uid);
                if (idx >= 0) setActiveIndex(idx);
              }}
              onFinalise={(report) => {
                console.info("[pathology] synoptic report finalised", report);
              }}
            />
          </Show>

          {/* ---------------- Endoscopy (video) ---------------- */}
          {/* Defer mounting EndoscopyShell until the instances resource
              resolves (success or error). That way the shell sees a
              non-empty props.instances on first render, the video-url
              createResource latches onto a real activeInstance, and
              the <video> element reaches `readyState ≥ 1` before any
              downstream effect has a chance to re-fire. Unconditional
              mount loses the first-render race on this route. */}
          <Show when={specialty() === "endoscopy" && !instances.loading}>
            <EndoscopyShell
              studyUid={params.studyUid}
              patientName={activeStudy()?.patientName}
              patientId={activeStudy()?.patientId}
              procedure={guessEndoProc(
                activeInstance()?.seriesDescription,
                activeStudy()?.description,
                activeStudy()?.patientName,
              )}
              instances={instances() ?? []}
              activeInstance={activeInstance()}
              onPickInstance={(uid) => {
                const list = instances() ?? [];
                const idx = list.findIndex((x) => x.instanceUid === uid);
                if (idx >= 0) setActiveIndex(idx);
              }}
            />
          </Show>

          {/* ---------------- Radiotherapy (MPR + DVH) ---------------- */}
          <Show when={specialty() === "radiotherapy"}>
            <Show when={volume()} fallback={
              <div class="grid h-full place-items-center">
                <div class="w-[320px] rounded border border-ink-300 bg-ink-100 p-4 text-xs">
                  <div class="mb-2 text-ink-900">
                    Building RT volume
                    <Show when={volProgress()}>
                      {" "}<span class="font-mono text-brand">
                        {volProgress()!.loaded} / {volProgress()!.total}
                      </span>
                    </Show>
                  </div>
                  <div class="h-1.5 w-full overflow-hidden rounded bg-ink-200">
                    <div class="h-full bg-brand transition-all"
                         style={{
                           width: volProgress()
                             ? `${Math.round(volProgress()!.loaded / Math.max(1, volProgress()!.total) * 100)}%`
                             : "8%",
                         }} />
                  </div>
                  <div class="mt-2 text-[10px] text-ink-700">
                    <Show when={volError()}>error — {volError()}</Show>
                  </div>
                </div>
              </div>
            }>
              {(vol) => <MPRViewport volume={vol()} />}
            </Show>
          </Show>

          {/* 3D ray-marcher — sits in place of the 2D canvas when the
              user toggles the 3 button. Build progress + errors are
              displayed in the same viewport area so the UX mirrors 2D
              loading state. */}
          <Show when={specialty() === "radiology" && mode3D()}>
            <Show when={volume()} fallback={
              <div class="grid h-full place-items-center">
                <div class="w-[320px] rounded border border-ink-300 bg-ink-100 p-4 text-xs">
                  <Show when={volError()} fallback={
                    <>
                      <div class="mb-2 text-ink-900">
                        Building volume
                        <Show when={volProgress()}>
                          {" "}<span class="font-mono text-brand">
                            {volProgress()!.loaded} / {volProgress()!.total}
                          </span>
                        </Show>
                      </div>
                      <div class="h-1.5 w-full overflow-hidden rounded bg-ink-200">
                        <div class="h-full bg-brand transition-all"
                             style={{
                               width: volProgress()
                                 ? `${Math.round(volProgress()!.loaded / Math.max(1, volProgress()!.total) * 100)}%`
                                 : "8%",
                             }} />
                      </div>
                      <div class="mt-2 text-[10px] text-ink-700">
                        <Show when={volProgress()?.phase === "assembling"}>
                          assembling 3-D texture…
                        </Show>
                        <Show when={!volProgress() || volProgress()!.phase === "loading"}>
                          downloading + decoding slices…
                        </Show>
                      </div>
                    </>
                  }>
                    <div class="mb-1 font-semibold text-danger">Couldn't build volume</div>
                    <div class="text-[11px] text-ink-800">{volError()}</div>
                    <button class="mt-2 btn-ghost text-xs" onClick={() => toggle3D()}>
                      back to 2D
                    </button>
                  </Show>
                </div>
              </div>
            }>
              {(vol) => (
                <>
                  <Canvas3D
                    volume={vol()}
                    wc={volWc()}
                    ww={volWw()}
                    mode={volMode()}
                    preset={volPreset()}
                    onWindowChange={(wc, ww) => {
                      setVolWc(wc);
                      setVolWw(ww);
                    }}
                  />
                  {/* Preset strip at the top of the viewport. Preset
                      chips on the left, composite-mode switch in the
                      middle, live W/L editors on the right. W/L also
                      updates when the user right-drags the volume. */}
                  <div class="pointer-events-none absolute inset-x-0 top-0 flex flex-wrap items-center gap-1 border-b border-ink-300 bg-ink-100/90 px-3 py-1.5 text-[11px]">
                    <span class="text-ink-700">preset:</span>
                    <For each={CT_PRESETS}>{(p) => (
                      <button type="button"
                              class="pointer-events-auto rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider transition"
                              classList={{
                                "bg-brand text-white border-brand": volPreset().name === p.name,
                                "border-ink-300 text-ink-700 hover:text-white": volPreset().name !== p.name,
                              }}
                              title={`${p.mode.toUpperCase()} · W/L ${p.wc}/${p.ww}`}
                              onClick={() => applyVolPreset(p)}>
                        {p.name}
                      </button>
                    )}</For>
                    <span class="ml-2 text-ink-700">mode:</span>
                    <div class="pointer-events-auto inline-flex rounded border border-ink-300">
                      {(["mip", "dvr", "minip"] as const).map((m) => (
                        <button type="button"
                                class="px-2 py-0.5 text-[10px] uppercase"
                                classList={{
                                  "bg-brand text-white": volMode() === m,
                                  "text-ink-700 hover:text-white": volMode() !== m,
                                }}
                                onClick={() => setVolMode(m)}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <span class="ml-2 text-ink-700">W/L</span>
                    <input
                      type="number"
                      class="pointer-events-auto w-[4.5rem] rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                      value={Math.round(volWc())}
                      title="Window center (HU)"
                      step="1"
                      onInput={(e) => {
                        const n = parseFloat((e.currentTarget as HTMLInputElement).value);
                        if (Number.isFinite(n)) setVolWc(n);
                      }}
                    />
                    <span class="text-ink-700">/</span>
                    <input
                      type="number"
                      class="pointer-events-auto w-[4.5rem] rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                      value={Math.round(volWw())}
                      title="Window width (HU)"
                      min="1" step="1"
                      onInput={(e) => {
                        const n = parseFloat((e.currentTarget as HTMLInputElement).value);
                        if (Number.isFinite(n) && n >= 1) setVolWw(n);
                      }}
                    />
                    <button type="button"
                            class="pointer-events-auto rounded border border-ink-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-700 hover:text-white"
                            title="Reset W/L to the active preset"
                            onClick={() => { setVolWc(volPreset().wc); setVolWw(volPreset().ww); }}>
                      reset
                    </button>
                  </div>
                </>
              )}
            </Show>
          </Show>

          <Show when={specialty() === "radiology" && !mode3D() && pixels()}
                fallback={
                  <Show when={specialty() === "radiology" && !mode3D()}>
                    <div class="grid h-full place-items-center text-ink-700">
                      <Show when={!pixelsLoading() && !pixelsError()} fallback={
                        <div class="text-sm">
                          <Show when={pixelsLoading()}>loading instance…</Show>
                          <Show when={pixelsError()}>
                            <div class="max-w-md rounded border border-danger/50 bg-danger/10 p-4 text-danger">
                              Decode failed — {pixelsError()}
                            </div>
                          </Show>
                        </div>
                      }>
                        <span>select an instance</span>
                      </Show>
                    </div>
                  </Show>
                }>
            <Canvas
              pixels={pixels()!}
              viewport={viewport}
              windowCenter={effectiveWC()}
              windowWidth={effectiveWW()}
              activeTool={activeTool() as any}
              onWindowChange={onWindowChange}
            />
            <MaskCanvas
              viewport={viewport}
              instanceUid={activeInstance()?.instanceUid ?? ""}
              imageW={pixels()!.width}
              imageH={pixels()!.height}
              activeTool={activeTool() as any}
              brushRadius={brushRadius()}
            />
            <AnnotationLayer
              viewport={viewport}
              measurements={measurements()}
              onAdd={(m) => setMeasurements((arr) => [...arr, m])}
              onDelete={(id) => setMeasurements((arr) => arr.filter((m) => m.id !== id))}
              instanceUid={activeInstance()?.instanceUid ?? ""}
              activeTool={(canMeasure() ? activeTool() : "none") as any}
              stored={pixels()!.stored as any}
              rescaleSlope={pixels()!.rescaleSlope}
              rescaleIntercept={pixels()!.rescaleIntercept}
              pixelSpacingMm={pixels()!.pixelSpacing as [number, number] | undefined}
              onPointerMove={(ix, iy) => {
                const p = pixels()!;
                if (ix < 0 || iy < 0 || ix >= p.width || iy >= p.height) { setHU(null); return; }
                const xi = Math.floor(ix), yi = Math.floor(iy);
                const stored = p.stored as Uint16Array | Int16Array | Uint8Array | undefined;
                if (!stored) return;
                const raw = stored[yi * p.width + xi];
                const val = raw * p.rescaleSlope + p.rescaleIntercept;
                setHU({ x: Math.round(ix), y: Math.round(iy), hu: val });
              }}
            />
            {/* Loading overlay — shown whenever a fresh slice is
                fetching. We keep the previous slice on screen to
                preserve spatial context; this banner + spinner tells
                the user the new slice isn't ready yet. Threshold is
                ~60 ms so a hot cache hit doesn't flash the overlay. */}
            <Show when={pixelsLoading()}>
              <div class="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full border border-brand/60 bg-black/75 px-3 py-1 font-mono text-[10px] text-white/90 shadow">
                <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-brand align-middle" />
                <span class="ml-2 align-middle">
                  loading slice <span class="text-brand">{activeIndex() + 1}</span>
                  {" / "}
                  <span class="text-brand">{instances()?.length ?? 0}</span>
                </span>
              </div>
            </Show>
            <Show when={pixelsError()}>
              <div class="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded border border-danger/60 bg-black/80 px-3 py-1 font-mono text-[10px] text-danger shadow">
                decode failed — {pixelsError()}
              </div>
            </Show>
            {/* HUD — patient/laterality watermarks */}
            <div class="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80">
              STUDY <span class="text-white">{activeInstance()?.studyUid.slice(-18) ?? "—"}</span>
              <span class="ml-2">{activeModality() || "—"}</span>
            </div>
            <div class="pointer-events-none absolute right-2 top-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-warn">
              CHECK PATIENT + LATERALITY
            </div>
            <Show when={hu()}>
              <div class="pointer-events-none absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/90">
                ({hu()!.x}, {hu()!.y})  ·  {hu()!.hu.toFixed(0)} HU
              </div>
            </Show>
            <div class="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80 leading-4">
              Tool: <span class="text-brand">{activeTool() === "none" ? "—" : activeTool()}</span>
              {" "}· idx <span class="text-brand">{activeIndex() + 1}/{instances()?.length ?? 0}</span>
              {" "}· cached <span class="text-brand">{cacheStats.cacheSize()}</span>
              {" "}· in-flight <span class="text-brand">{cacheStats.inflight()}</span>
              <br />
              hits <span class="text-brand">{cacheStats.hits}</span>
              {" "}miss <span class="text-brand">{cacheStats.misses}</span>
              {" "}· last fetch <span class="text-brand">{cacheStats.fetchMs.toFixed(0)} ms</span>
              {" "}decode <span class="text-brand">{cacheStats.decodeMs.toFixed(0)} ms</span>
              {" "}· worker <span class="text-brand">{cacheStats.workerOk ? "on" : "OFF"}</span>
              {" "}· warmer <span class="text-brand">{cacheStats.warmer().active ? "active" : "idle"}</span>
            </div>
          </Show>
        </div>
      </div>

      {/* AI Report draft panel — right-docked. MedDream has a report
          drawer; we take a2zradiology.ai's preliminary-draft angle.
          Hidden for endoscopy because EndoscopyShell ships its own
          right-dock report panel with anatomy-span log + captured-
          stills grid + PDF-export button; stacking both would squeeze
          the video viewport into a narrow strip. */}
      <Show when={specialty() !== "endoscopy" && specialty() !== "pathology"}>
        <ReportDraftPanel
          studyUid={params.studyUid}
          specialty={specialty()}
          collapsed={aiPanelCollapsed()}
          onCollapseChange={setAiPanelCollapsed}
          onJumpTo={(uid, f) => jumpToInstance(uid, f)}
          onSign={async (_body) => setReportOpen(true)}
        />
      </Show>

      <ReportPanel
        studyUid={params.studyUid}
        mrn={""}
        patientName={""}
        measurements={measurements()}
        open={reportOpen()}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
}

